import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  ClockIcon,
  CalendarDaysIcon,
  UserGroupIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  PlayIcon,
  PauseIcon
} from '@heroicons/react/24/outline';
import { formatDistanceToNow } from 'date-fns';

interface Project {
  id: string;
  title: string;
  status: 'active' | 'in_progress' | 'completed' | 'on_hold';
  due_date: string;
  collaborator: string;
  collaborator_avatar?: string;
  progress?: number;
  priority?: 'low' | 'medium' | 'high';
}

interface ProjectManagementProps {
    projects: Project[];
  userType: 'creator' | 'model' | 'hirer';
}

const ProjectManagement: React.FC<ProjectManagementProps> = ({ 
  projects, 
  userType 
}) => {
    // // console.log(typeof projects[0].due_date, projects[0].due_date);

    // // console.log(projects[0], new Date(projects[0].due_date))
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return PlayIcon;
      case 'in_progress':
        return ClockIcon;
      case 'completed':
        return CheckCircleIcon;
      case 'on_hold':
        return PauseIcon;
      default:
        return ClockIcon;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'text-blue-600 bg-blue-100 dark:text-blue-400 dark:bg-blue-900/30';
      case 'in_progress':
        return 'text-orange-600 bg-orange-100 dark:text-orange-400 dark:bg-orange-900/30';
      case 'completed':
        return 'text-green-600 bg-green-100 dark:text-green-400 dark:bg-green-900/30';
      case 'on_hold':
        return 'text-gray-600 bg-gray-100 dark:text-gray-400 dark:bg-gray-800';
      default:
        return 'text-gray-600 bg-gray-100 dark:text-gray-400 dark:bg-gray-800';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'border-l-red-500';
      case 'medium':
        return 'border-l-orange-500';
      case 'low':
        return 'border-l-green-500';
      default:
        return 'border-l-gray-300';
    }
  };

  const isOverdue = (due_date: string) => {
    return new Date(due_date) < new Date();
  };

  return (
    <div className="bg-white rounded-2xl p-4 md:p-6 shadow-sm border border-gray-100 dark:bg-gray-900 dark:border-gray-800">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-base md:text-lg font-bold text-gray-900 dark:text-white">
          {userType === 'creator' ? 'Active Shoots' : 'Active Projects'}
        </h2>
        <Link 
          to="/opportunities"
          className="text-sm text-teal-600 hover:text-teal-700 font-medium dark:text-teal-400 dark:hover:text-teal-300"
        >
          View All →
        </Link>
      </div>

      <div className="space-y-4 max-h-96 overflow-y-auto">
        {projects.length > 0 ? (
          projects.map((project, index) => {
            const StatusIcon = getStatusIcon(project.status);
            const statusColor = getStatusColor(project.status);
            const priorityColor = getPriorityColor(project.priority || 'medium');
            const overdue = isOverdue(project.due_date);

            return (
              <motion.div
                key={project.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`p-3 md:p-4 border-l-4 rounded-lg hover:shadow-md transition-all duration-200 ${priorityColor} ${
                  overdue ? 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800' : 'bg-gray-50 border-gray-200 dark:bg-gray-800 dark:border-gray-700'
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <Link 
                      to={`/opportunities/${project.id}`}
                      className="text-sm md:text-base font-semibold text-gray-900 hover:text-teal-600 transition-colors line-clamp-1 dark:text-white dark:hover:text-teal-400"
                    >
                      {project.title}
                    </Link>
                    
                    {/* Collaborator Info */}
                    <div className="flex items-center space-x-2 mt-1">
                      {project.collaborator_avatar ? (
                        <img
                          src={project.collaborator_avatar}
                          alt={project.collaborator}
                          className="w-5 h-5 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-5 h-5 rounded-full bg-gray-300 flex items-center justify-center dark:bg-gray-600">
                          <span className="text-xs font-medium text-gray-600 dark:text-gray-200">
                            {project.collaborator.charAt(0).toUpperCase()}
                          </span>
                        </div>
                      )}
                      <span className="text-xs md:text-sm text-gray-600 dark:text-gray-400">{project.collaborator}</span>
                    </div>
                  </div>

                  {/* Status Badge */}
                  <div className={`inline-flex items-center px-2 py-1 rounded-full text-[10px] md:text-xs font-medium ${statusColor}`}>
                    <StatusIcon className="h-3 w-3 mr-1" />
                    {project.status.replace('_', ' ')}
                  </div>
                </div>

                {/* Due Date & Priority */}
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center space-x-4">
                    <div className={`flex items-center space-x-1 ${overdue ? 'text-red-600 dark:text-red-400' : 'text-gray-600 dark:text-gray-400'}`}>
                      <CalendarDaysIcon className="h-4 w-4" />
                      <span>
                        {overdue && <ExclamationTriangleIcon className="h-3 w-3 inline text-red-500 mr-1" />}
                        {formatDistanceToNow(new Date(project.due_date), { addSuffix: true })}
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })
        ) : (
          <div className="text-center py-8">
            <UserGroupIcon className="h-12 w-12 text-gray-300 mx-auto mb-3 dark:text-gray-600" />
            <p className="text-gray-500 text-sm dark:text-gray-400">No active projects</p>
            <p className="text-gray-400 text-xs mt-1 dark:text-gray-500">
              {userType === 'creator' 
                ? 'Start collaborating with models to see your shoots here'
                : 'Apply to opportunities to see your projects here'
              }
            </p>
            <Link 
              to={userType === 'creator' || userType === 'hirer' ? '/opportunities/post' : '/opportunities'}
              className="inline-block mt-3 text-sm text-teal-600 hover:text-teal-700 font-medium dark:text-teal-400 dark:hover:text-teal-300"
            >
              {userType === 'creator' || userType === 'hirer' ? 'Post Opportunity →' : 'Browse Opportunities →'}
            </Link>
          </div>
        )}
      </div>

      {/* Quick Stats */}
      {projects.length > 0 && (
        <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-800">
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-lg font-bold text-teal-600 dark:text-teal-400">
                {projects.filter(p => p.status === 'active' || p.status === 'in_progress').length}
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">Active</div>
            </div>
            
            <div>
              <div className="text-lg font-bold text-orange-600 dark:text-orange-400">
                {projects.filter(p => isOverdue(p.due_date) && p.status !== 'completed').length}
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">Overdue</div>
            </div>
            
            <div>
              <div className="text-lg font-bold text-green-600 dark:text-green-400">
                {projects.filter(p => p.status === 'completed').length}
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">Completed</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectManagement;
